import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useMemo } from "react";
import RenderWithTooltip from "@/components/RenderWithTooltip";
import StatusChip from "@/components/StatusChip";
import ActionMenu from "@/components/app/ActionMenu";
import { AppointmentSchedulingType } from "@/dummy-data/appointment-list-data-scheduling";
import { Appointment } from "@/services";
import { formatDateTime } from "@/utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "@/utils/utils";
import {
  APPOINTMENT_STATUS_COLOR,
  APPOINTMENT_STATUS_RECORD,
  COMPLETE_VISIT_DISABLED_MSG,
  COMPLETE_VISIT_ENABLED_STATUSES,
  VIEW_VISIT_ENABLED_STATUSES,
  VISIT_HISTORY_ACTION,
  VISIT_HISTORY_LABELS,
} from "../visit-detail-constant";

export interface UseVisitHistoryColumnsParams {
  showLastUpdated: boolean;
  hasUpdate: boolean;
  hasReadOnly: boolean;
  onView: (appointmentId: string) => void;
  onEdit: (appointmentId: string) => void;
  onDelete: (appointmentId: string) => void;
}

const canView = (status?: Appointment["status"]) =>
  !!status && VIEW_VISIT_ENABLED_STATUSES.includes(status);

const canEdit = (status?: Appointment["status"]) =>
  !!status && COMPLETE_VISIT_ENABLED_STATUSES.includes(status);

export const useVisitHistoryColumns = ({
  showLastUpdated,
  hasUpdate,
  hasReadOnly,
  onView,
  onEdit,
  onDelete,
}: UseVisitHistoryColumnsParams): GridColDef<Appointment>[] =>
  useMemo(
    () =>
      applyDefaultColumnSettings<Appointment>(
        [
          {
            field: "appointmentDate",
            headerName: "Visit Date & Time",
            flex: 1,
            minWidth: 150,
            renderCell: ({ row }) =>
              row?.startTime ? formatDateTime(row?.startTime) : "-",
          },
          {
            field: "providerName",
            headerName: "Provider",
            flex: 0.8,
            minWidth: 130,
            renderCell: ({ value }) => <RenderWithTooltip value={value} />,
          },
          {
            field: "type",
            headerName: "Type",
            flex: 0.6,
            minWidth: 100,
            renderCell: ({ value }) => (
              <RenderWithTooltip
                value={
                  value ? replaceUnderScoreAddSpace(value.toLowerCase()) : "-"
                }
              />
            ),
          },
          {
            field: "chiefComplaint",
            headerName: "Reason for Visit",
            flex: 1.2,
            minWidth: 160,
            renderCell: ({ value }) => <RenderWithTooltip value={value} />,
          },
          {
            field: "status",
            headerName: "Appt. Status",
            flex: 0.6,
            minWidth: 110,
            renderCell: ({ value }) => {
              if (!value) return "-";
              const status = value as AppointmentSchedulingType["status"];
              const colors = APPOINTMENT_STATUS_COLOR[status];
              return (
                <StatusChip
                  label={APPOINTMENT_STATUS_RECORD[status]}
                  color={colors?.color}
                  bgColor={colors?.bgColor}
                />
              );
            },
          },
          ...(showLastUpdated
            ? [
                {
                  field: "modified",
                  flex: 0.8,
                  minWidth: 140,
                  headerName: "Last Updated",
                  sortable: false,
                  renderCell: ({
                    value,
                  }: GridRenderCellParams<Appointment, string>) => (
                    <RenderWithTooltip value={formatDateTime(value)} />
                  ),
                },
              ]
            : []),
          {
            field: "Action",
            headerName: "Actions",
            align: "center",
            headerAlign: "center",
            hideSortIcons: true,
            renderCell: ({ row }) => (
              <ActionMenu
                options={roleFilter({
                  list: [
                    {
                      value: VISIT_HISTORY_ACTION.VIEW,
                      label: VISIT_HISTORY_LABELS.viewVisit,
                      isDisabled: !canView(row?.status),
                      hoverMsg: VISIT_HISTORY_LABELS.visitNotCompleted,
                    },
                    {
                      value: VISIT_HISTORY_ACTION.EDIT,
                      label: VISIT_HISTORY_LABELS.completeVisit,
                      isDisabled: !canEdit(row?.status),
                      hoverMsg: COMPLETE_VISIT_DISABLED_MSG,
                    },
                  ],
                  compWith: { [VISIT_HISTORY_ACTION.VIEW]: hasUpdate },
                  removeOtherFields: hasReadOnly,
                })}
                onActionClick={(action) => {
                  if (!row?.uuid) return;
                  if (action === VISIT_HISTORY_ACTION.VIEW) onView(row.uuid);
                  else if (action === VISIT_HISTORY_ACTION.EDIT)
                    onEdit(row.uuid);
                  else if (action === VISIT_HISTORY_ACTION.DELETE)
                    onDelete(row.uuid);
                }}
              />
            ),
          },
        ],
        ["appointmentDate"]
      ),
    [showLastUpdated, hasUpdate, hasReadOnly, onView, onEdit, onDelete]
  );
